import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, BarChart, CheckCircle } from 'lucide-react';

interface PlanSelectionProps {
  onPlanSelected: (plan: 'stats' | 'premium') => void;
  selectedPlan: 'stats' | 'premium' | null;
}

export const PlanSelection: React.FC<PlanSelectionProps> = ({ onPlanSelected, selectedPlan }) => {
  useEffect(() => {
    // Charger le script Stripe
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const plans = [
    {
      id: 'stats' as const,
      title: 'Plan Stats',
      price: '4,90€',
      description: 'Pour l\'analyse et le suivi',
      icon: BarChart,
      color: 'from-blue-500 to-blue-600',
      features: [
        'Page Mes Tableaux',
        'Gestion de l\'abonnement',
        'Paramètres basiques',
        'Support par email'
      ],
      limitations: [
        'Pas de mode Caisse',
        'Pas de mode Retrait',
        'Fonctionnalités limitées'
      ]
    },
    {
      id: 'premium' as const,
      title: 'Plan Premium',
      price: '39,90€',
      description: 'Accès complet à toutes les fonctionnalités',
      icon: Crown,
      color: 'from-amber-500 to-orange-500',
      features: [
        'Tous les tableaux et analyses',
        'Mode Caisse OU Mode Retrait',
        'Gestion d\'équipe',
        'Export/Import de données',
        'Historique complet',
        'Support prioritaire',
        'Toutes les fonctionnalités'
      ],
      recommended: true
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-2">
          Choisissez votre plan
        </h2>
        <p className="text-muted-foreground">
          Sélectionnez le plan qui correspond à vos besoins
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const isSelected = selectedPlan === plan.id;
          
          return (
            <Card 
              key={plan.id}
              className={`
                relative cursor-pointer transition-all duration-300 transform hover:scale-105
                ${isSelected ? 'ring-4 ring-primary shadow-xl' : 'hover:shadow-lg'}
                ${plan.recommended ? 'border-2 border-amber-400' : ''}
              `}
              onClick={() => onPlanSelected(plan.id)}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-lg">
                    Recommandé
                  </span>
                </div>
              )}
              
              <CardHeader className="relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${plan.color} opacity-10`} />
                
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${plan.color} text-white shadow-lg`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    {isSelected && (
                      <div className="p-2 rounded-full bg-green-100 dark:bg-green-900">
                        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                    )}
                  </div>
                  
                  <CardTitle className="text-xl mb-1">{plan.title}</CardTitle>
                  <CardDescription className="text-sm">{plan.description}</CardDescription>
                  
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-sm text-muted-foreground">/mois</span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="relative space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2 text-green-600">Inclus :</h4>
                  <ul className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {plan.limitations && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 text-muted-foreground">Limitations :</h4>
                    <ul className="space-y-1">
                      {plan.limitations.map((limitation, index) => (
                        <li key={index} className="text-xs text-muted-foreground">
                          • {limitation}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {isSelected && (
                  <div className="absolute inset-0 bg-primary/5 rounded-lg pointer-events-none" />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="text-center">
        <p className="text-xs text-muted-foreground">
          Essai démo de 7 jours inclus • Sans engagement • Annulation à tout moment
        </p>
      </div>
    </div>
  );
};
